import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Building2, Target } from 'lucide-react';
import StrategyWorkspaceHeader from './StrategyWorkspaceHeader';
import { useInternalAssessmentProgress } from '../../hooks/useInternalAssessmentProgress';
import SwotSection from './internal-assessment/SwotSection';
import IndustrySuccessFactorsSection from './internal-assessment/IndustrySuccessFactorsSection';

export default function InternalAssessmentPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('swot');
  const { progress: internalProgress, refreshProgress } = useInternalAssessmentProgress(
    companyId || ''
  );

  useEffect(() => {
    setActiveSection(
      location.hash === '#industrySuccessFactors' ? 'industrySuccessFactors' : 'swot'
    );
  }, [location.hash, companyId]);

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    navigate({ pathname: location.pathname, hash: `#${section}` }, { replace: true });
  };

  const handleProgressUpdate = useCallback(() => {
    refreshProgress();
  }, [refreshProgress]);

  return (
    <div className="company-workspace-screen strategy-editor-page">
      <main>
        <StrategyWorkspaceHeader
          companyId={companyId || ''}
          step={3}
          stage="Internal assessment"
          title="Know where you can win."
          description="Find the strengths to build on and the capabilities your company needs to develop."
          progress={internalProgress.overall}
          activeSection={activeSection}
          sections={[
            {
              id: 'swot',
              label: 'SWOT analysis',
              icon: Building2,
              progress: internalProgress.swot,
            },
            {
              id: 'industrySuccessFactors',
              label: 'Success factors',
              icon: Target,
              progress: internalProgress.industrySuccessFactors,
            },
          ]}
          onSectionChange={handleSectionChange}
          next={{
            label: 'Choose your strategy',
            href: `/company/${companyId}/strategy/formulation/summary`,
          }}
        />
        <div className="strategy-editor-content">
          {activeSection === 'swot' && (
            <SwotSection companyId={companyId!} onProgressChange={handleProgressUpdate} />
          )}
          {activeSection === 'industrySuccessFactors' && (
            <IndustrySuccessFactorsSection
              companyId={companyId!}
              onProgressChange={handleProgressUpdate}
            />
          )}
        </div>
      </main>
    </div>
  );
}
